// ===========================================
// useTermLock
// ===========================================
// Page-level lock lookups for the dialogs and pickers (the Terms page reads the server's `lock`
// instead). Uses the SCHOOL's day via `useSchoolTimezone` — which THROWS outside a TenantProvider,
// so call it once at page level and pass `isLocked` down; the pure helpers stay provider-free.

import { useCallback, useMemo } from 'react';
import { useSchoolTimezone } from '../contexts/TenantContext';
import { schoolTodayUtcMidnight } from '../utils/dateUtils';
import { termLockState, type LockableTermLike, type TermLockState } from '../utils/termLock';

export function useTermLock<T extends LockableTermLike>(terms: readonly T[] | undefined) {
  const timeZone = useSchoolTimezone();
  const list = useMemo(() => terms ?? [], [terms]);
  // The school day is computed on EVERY render (cheap) so a page left open across school midnight
  // flips on its next render; the predicate is keyed on the day's value, not a fresh Date object.
  const schoolToday = schoolTodayUtcMidnight(timeZone);
  const dayKey = schoolToday.getTime();

  const stateOf = useCallback(
    (termOrId: T | string): TermLockState<T> | undefined => {
      const term = typeof termOrId === 'string' ? list.find((t) => t.id === termOrId) : termOrId;
      return term ? termLockState(term, list, new Date(dayKey)) : undefined;
    },
    [list, dayKey]
  );

  const isLocked = useCallback(
    (termOrId: T | string): boolean => stateOf(termOrId)?.status === 'LOCKED',
    [stateOf]
  );

  return { stateOf, isLocked, schoolToday };
}
